<template>
  <div class="c-component-theme-options pa-2 flex flex-col gap-4 max-h-[400px] overflow-y-auto">
    <div v-if="showColorPickerComponent" class="flex flex-col gap-2">
      <span class="inline-block leading-5 text-base font-normal" :style="{ color: themeVars.text }">
        {{ t('canvasUI.canvasBuilder.canvasDrawerTheme.backgroundColor') }}
      </span>
      <CSwatchSelector
        v-if="enableCanvasTokens"
        :colors="palette"
        include-transparent
        :modelValue="backgroundColor"
        @update:modelValue="(value: string) => onBgPropertyChange('backgroundColor', value)"
      />
      <NColorPicker
        v-else
        :actions="['clear', 'confirm']"
        default-value=""
        :modes="['hex']"
        :on-update:value="(value: string) => onBgPropertyChange('backgroundColor', value)"
        :swatches="palette"
        :updateInputModelOnDrop="true"
        :value="backgroundColor"
      />

      <div
        v-if="
          !!launchDarkly.enable_background_image_theme_options &&
          (componentNode?.type === ComponentTypes.Grid ||
            componentNode?.type === ComponentTypes.GridItem ||
            componentNode?.type === ComponentTypes.Scribble)
        "
        class="flex flex-col gap-2 mt-2"
      >
        <CBackgroundImageThemeOptions
          :background-attachment="modelValue.backgroundAttachment"
          :background-image="modelValue.backgroundImage"
          :background-position-x="modelValue.backgroundPositionX"
          :background-position-y="modelValue.backgroundPositionY"
          :background-repeat="modelValue.backgroundRepeat"
          :background-size="modelValue.backgroundSize"
          :hide-background-attachment="true"
          :hide-background-position-x="isScribble"
          :hide-background-position-y="isScribble"
          :hide-background-repeat="isScribble"
          :hide-background-size="isScribble"
          @bg-property-change="onBgPropertyChange"
          @choose-content="emit('choose-content')"
        />
      </div>
    </div>

    <div v-if="showSpacingComponent" class="flex flex-col gap-2">
      <span class="inline-block leading-5 text-base font-normal">
        {{ t('canvasUI.canvasBuilder.canvasDrawerTheme.componentSpacing.title') }}: {{ modelValue.spacing }}px
      </span>
      <CSlider
        :format-tooltip="(value: number) => `${value}px`"
        :step="DEFAULT_GLOBAL_COMPONENT_SPACING_INTERVAL"
        :value="modelValue.spacing ?? 0"
        @update:value="(value: number) => onSpacingChange(value)"
      >
        <template #helpText>
          <div>
            <span
              class="underline cursor-pointer"
              :style="{
                color: themeVars.primary,
              }"
              @click="onSpacingReset"
              >{{ t('canvasUI.common.reset') }}
            </span>
            <span>&nbsp;</span>
            <span>{{ t('canvasUI.canvasBuilder.canvasDrawerTheme.componentSpacing.helpText') }}: </span>
            <span>{{ globalComponentSpacing }}px</span>
          </div>
        </template>
      </CSlider>
    </div>

    <div v-if="showPaddingComponent" class="flex flex-col gap-2">
      <span class="inline-block leading-5 text-base font-normal">
        {{ t('canvasUI.canvasBuilder.canvasDrawerTheme.padding.title') }}
      </span>
      <CPaddingInput
        :is-advanced-mode="showPaddingInAdvancedMode"
        :values="modelValue.padding"
        @update:value="(value: ThemePadding) => onPaddingChange(value)"
      />
    </div>

    <div v-if="showBorderRadiusComponent" class="flex flex-col gap-2">
      <span class="inline-block leading-5 text-base font-normal">
        {{ t('canvasUI.canvasBuilder.canvasDrawerTheme.borderRadius.title') }}
      </span>
      <CBorderRadiusInput
        :is-advanced-mode="showBorderRadiusInAdvancedMode"
        :values="modelValue.borderRadius"
        @update:value="(value: ThemeBorderRadius) => onBorderRadiusChange(value)"
      />
    </div>

    <CHeightInput
      v-if="showHeightComponent"
      default-value="400px"
      :model-value="modelValue.height"
      @update:model-value="onHeightChange"
    />
  </div>
</template>
<script lang="ts" setup>
import { NColorPicker } from 'naive-ui'
import { computed, inject, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { isNil } from 'lodash-es'

import CBackgroundImageThemeOptions from '@lib/components/CBackgroundImageThemeOptions/CBackgroundImageThemeOptions.vue'
import CHeightInput from '@lib/components/CHeightInput/CHeightInput.vue'
import CPaddingInput from '@lib/components/CPaddingInput/CPaddingInput.vue'
import CSlider from '@lib/components/CSlider/CSlider.vue'
import CSwatchSelector from '@lib/components/CSwatchSelector/CSwatchSelector.vue'
import CBorderRadiusInput from '@lib/components/CBorderRadiusInput/CBorderRadiusInput.vue'

import useThemeVars from '@lib/composables/useThemeVars'
import useCanvas from '@lib/apps/canvas-builder/composables/useCanvas'
import useCanvasTheme, {
  ThemeComponentOptionEnum,
  type ThemePadding,
  type ThemeBorderRadius,
} from '@lib/apps/canvas-builder/composables/useCanvasTheme'

import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import { ComponentTypes, type ComponentNode } from '@canvas-builder/types/canvas'
import type {
  BackgroundAttachment,
  BackgroundPositionX,
  BackgroundPositionY,
  BackgroundRepeat,
  BackgroundSize,
} from '@lib/types/background'
import { DEFAULT_GLOBAL_COMPONENT_SPACING_INTERVAL } from '@canvas-builder/constants/theme.const'

const props = defineProps<{
  modelValue: {
    backgroundColor?: string
    backgroundAttachment?: BackgroundAttachment
    backgroundImage?: string
    backgroundPositionX?: BackgroundPositionX
    backgroundPositionY?: BackgroundPositionY
    backgroundRepeat?: BackgroundRepeat
    backgroundSize?: BackgroundSize
    height?: string | null
    padding?: ThemePadding
    spacing?: number | null
    borderRadius?: ThemeBorderRadius
  }
  componentNode?: ComponentNode
  excludeStylables?: ThemeComponentOptionEnum[]
}>()

const emit = defineEmits<{
  'update:modelValue': [value: typeof props.modelValue]
  'choose-content': []
  'set-palette-index': [index: number | undefined]
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const { t } = useI18n()
const { areTokenConstraintsActive } = useCanvas()
const { globalComponentSpacing, palette } = useCanvasTheme()
const themeVars = useThemeVars()

const enableCanvasTokens = computed(() => !!launchDarkly.value.enable_canvas_tokens && areTokenConstraintsActive.value)

const backgroundColor = computed(() => {
  if (
    launchDarkly.value.enable_themes &&
    !isNil(props.componentNode?.theme_meta?.bg_color_palette_index) &&
    props.componentNode.theme_meta.bg_color_palette_index >= 0
  ) {
    return palette.value[props.componentNode.theme_meta.bg_color_palette_index]
  }
  return props.modelValue.backgroundColor
})

const isScribble = computed(() => {
  return props.componentNode?.type === ComponentTypes.Scribble
})

const showPaddingInAdvancedMode = computed(() => {
  return (
    props.modelValue.padding?.top !== props.modelValue.padding?.left ||
    props.modelValue.padding?.top !== props.modelValue.padding?.bottom ||
    props.modelValue.padding?.top !== props.modelValue.padding?.right
  )
})

const showColorPickerComponent = computed(
  () => !props.excludeStylables?.includes(ThemeComponentOptionEnum.COLOR_PICKER),
)

const showHeightComponent = computed(
  () => !!launchDarkly.value.enable_height_in_component_theming && props.componentNode?.type === ComponentTypes.Grid,
)

const showSpacingComponent = computed(() => !props.excludeStylables?.includes(ThemeComponentOptionEnum.SPACING))

const showPaddingComponent = computed(() => !props.excludeStylables?.includes(ThemeComponentOptionEnum.PADDING))

const showBorderRadiusComponent = computed(
  () =>
    !!launchDarkly.value.enable_theme_options_border_radius &&
    !props.excludeStylables?.includes(ThemeComponentOptionEnum.BORDER_RADIUS) &&
    (props.componentNode?.type === ComponentTypes.Grid ||
      props.componentNode?.type === ComponentTypes.GridItem ||
      props.componentNode?.type === ComponentTypes.Multimedia ||
      props.componentNode?.type === ComponentTypes.Text),
)

const showBorderRadiusInAdvancedMode = computed(() => {
  return !(
    props.modelValue.borderRadius?.topLeft === props.modelValue.borderRadius?.topRight &&
    props.modelValue.borderRadius?.topLeft === props.modelValue.borderRadius?.bottomLeft &&
    props.modelValue.borderRadius?.topLeft === props.modelValue.borderRadius?.bottomRight
  )
})

function updateForm(updates: Partial<typeof props.modelValue>) {
  emit('update:modelValue', { ...props.modelValue, ...updates })
}

function onBgPropertyChange(property: string, value: string) {
  if (launchDarkly.value.enable_themes) {
    const index = palette.value.findIndex((p) => p === value)

    emit('set-palette-index', index !== -1 ? index : undefined)
  }

  updateForm({ [property]: value })
}

function onHeightChange(value?: string | null) {
  updateForm({ height: value })
}

function onPaddingChange(value: ThemePadding) {
  updateForm({ padding: value })
}

function onSpacingChange(value: number) {
  updateForm({ spacing: value })
}

function onSpacingReset() {
  updateForm({ spacing: null })
}

function onBorderRadiusChange(value: ThemeBorderRadius) {
  updateForm({ borderRadius: value })
}
</script>
